import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getTool, createReport, downloadReportPdf } from '../lib/api';
import { isAuthenticated, getUser } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { formatCurrency, downloadBlob } from '../lib/utils';
import { ArrowLeft, FileText, Download, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function ToolRunner() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inputs, setInputs] = useState({});
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/org/login');
      return;
    }
    loadTool();
  }, [toolId, navigate]);

  const loadTool = async () => {
    setLoading(true);
    try {
      const response = await getTool(toolId);
      setTool(response.data);
      
      // Initialize inputs with defaults
      const initialInputs = {};
      Object.entries(response.data.inputs_schema || {}).forEach(([key, schema]) => {
        initialInputs[key] = '';
      });
      setInputs(initialInputs);
    } catch (err) {
      console.error('Failed to load tool:', err);
      setError('Tool not found');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value, schema) => {
    let processedValue = value;
    
    // Convert to appropriate type
    if (schema.type === 'integer') {
      processedValue = value === '' ? '' : parseInt(value, 10);
    } else if (schema.type === 'number') {
      processedValue = value === '' ? '' : parseFloat(value);
    }
    
    setInputs({ ...inputs, [key]: processedValue });
  };

  const validateInputs = () => {
    for (const [key, schema] of Object.entries(tool.inputs_schema || {})) {
      if (schema.required && (inputs[key] === '' || inputs[key] === undefined)) {
        return `${schema.label || key} is required`;
      }
      if (inputs[key] !== '' && inputs[key] !== undefined) {
        if (schema.min !== undefined && inputs[key] < schema.min) {
          return `${schema.label || key} must be at least ${schema.min}`;
        }
        if (schema.max !== undefined && inputs[key] > schema.max) {
          return `${schema.label || key} must be at most ${schema.max}`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      // Filter out empty optional inputs
      const filteredInputs = {};
      for (const [key, value] of Object.entries(inputs)) {
        if (value !== '' && value !== undefined) {
          filteredInputs[key] = value;
        }
      }

      const response = await createReport({
        tool_id: toolId,
        inputs_json: filteredInputs
      });
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!report) return;
    try {
      const response = await downloadReportPdf(report.id);
      downloadBlob(response.data, `report-${report.report_id}.pdf`);
    } catch (err) {
      alert('Failed to download report');
    }
  };

  const formatOutputValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }
    if (key.includes('loss') || key.includes('recovered') || key.includes('gain') || key.includes('cost')) {
      return formatCurrency(value);
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500">Loading tool...</div>
      </div>
    );
  }

  if (error && !tool) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <Button onClick={() => navigate('/tools')} variant="outline" className="mt-4 rounded-none">
            Back to Tools
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="tool-runner-page">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/tools" className="hover:text-zinc-700 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Tools
            </Link>
            <span className="breadcrumb-separator">/</span>
            <span>{tool?.name}</span>
          </div>

          {/* Tool Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="cert-id">{tool?.id}</span>
              <Badge variant="outline" className="rounded-none status-active">
                {tool?.status}
              </Badge>
            </div>
            <h1 className="page-title text-2xl">{tool?.name}</h1>
            <p className="text-zinc-500 mt-2">{tool?.description}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-zinc-500">
              <span>Standard: <span className="font-mono">{tool?.standard_id}</span></span>
              <span>Version: <span className="font-mono">{tool?.standard_version}</span></span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Form */}
            <div>
              <div className="border border-zinc-200 p-6">
                <h2 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Input Parameters
                </h2>

                {error && (
                  <div className="alert alert-error flex items-center gap-2 mb-4">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {Object.entries(tool?.inputs_schema || {}).map(([key, schema]) => (
                    <div key={key} className="form-field">
                      <Label className="form-label">
                        {schema.label || key}
                        {schema.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        type={schema.type === 'integer' || schema.type === 'number' ? 'number' : 'text'}
                        value={inputs[key] || ''}
                        onChange={(e) => handleInputChange(key, e.target.value, schema)}
                        className="form-input rounded-none"
                        placeholder={schema.description}
                        min={schema.min}
                        max={schema.max}
                        step="any"
                        required={schema.required}
                        data-testid={`input-${key}`}
                      />
                      {schema.description && (
                        <p className="form-hint">{schema.description}</p>
                      )}
                    </div>
                  ))}

                  <Button
                    type="submit"
                    className="w-full btn-primary rounded-none h-11 mt-6"
                    disabled={submitting}
                    data-testid="generate-report-btn"
                  >
                    {submitting ? 'Generating...' : 'Generate Report'}
                  </Button>
                </form>
              </div>
            </div>

            {/* Output / Report */}
            <div>
              {report ? (
                <div className="border border-emerald-200 bg-emerald-50 p-6" data-testid="report-output">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-semibold text-emerald-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Report Generated
                    </h2>
                  </div>

                  <div className="bg-white border border-emerald-200 divide-y divide-emerald-100">
                    <div className="p-3 flex justify-between">
                      <span className="text-sm text-zinc-500">Report ID</span>
                      <span className="font-mono text-sm" data-testid="report-id">{report.report_id}</span>
                    </div>
                    <div className="p-3 flex justify-between">
                      <span className="text-sm text-zinc-500">Standard</span>
                      <span className="text-sm">{report.standard_title}</span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-emerald-800 uppercase tracking-wide mb-3">Results</h3>
                    <div className="bg-white border border-emerald-200 divide-y divide-emerald-100">
                      {Object.entries(report.outputs_json || {}).map(([key, value]) => (
                        <div key={key} className="p-3 flex justify-between">
                          <span className="text-sm text-zinc-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          <span className="font-mono text-sm font-medium" data-testid={`output-${key}`}>
                            {formatOutputValue(key, value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleDownload}
                    className="w-full btn-primary rounded-none h-11 mt-6"
                    data-testid="download-report-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </Button>

                  <Button
                    onClick={() => navigate(`/reports/${report.report_id}`)}
                    variant="outline"
                    className="w-full rounded-none h-11 mt-2"
                    data-testid="view-report-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Report Details
                  </Button>
                </div>
              ) : (
                <div className="border border-zinc-200 bg-zinc-50 p-6 text-center">
                  <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                  <p className="text-zinc-500">
                    Enter parameters and generate a report to see results.
                  </p>
                </div>
              )}

              {/* Notes */}
              <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200">
                <p className="text-xs text-zinc-500">
                  <strong>Note:</strong> {tool?.computation_spec?.notes || 'Results are estimates based on disclosed inputs. Actual results may vary.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
